import { readUserScopedItem, userScopedStorage } from '@erikdakoda/ui/shared/userScopedStorage';

const CONVO_ARTIFACT_PANEL_KEY_PREFIX = 'ai-chat-artifact-panel:';

const memoryCache = new Map<string, string>();

/**
 * Builds the localStorage key for a convo's open artifact panel identifier.
 */
export function getConvoArtifactPanelKey(convoId: string): string {
  return `${CONVO_ARTIFACT_PANEL_KEY_PREFIX}${convoId}`;
}

/**
 * Reads the persisted open artifact identifier for a convo (memory cache first, then localStorage).
 */
export function getConvoArtifactPanel(convoId: string): string | null {
  const cached = memoryCache.get(convoId);
  if (cached !== undefined) {
    return cached.length > 0 ? cached : null;
  }

  if (typeof window === 'undefined') {
    return null;
  }

  const raw = readUserScopedItem(getConvoArtifactPanelKey(convoId));
  if (!raw) {
    return null;
  }

  memoryCache.set(convoId, raw);
  return raw;
}

/**
 * Persists the open artifact identifier for a convo in memory and localStorage.
 */
export function setConvoArtifactPanel(convoId: string, identifier: string): void {
  memoryCache.set(convoId, identifier);
  if (typeof window === 'undefined') {
    return;
  }
  userScopedStorage.setItem(getConvoArtifactPanelKey(convoId), identifier);
}

/**
 * Removes persisted open artifact state for a convo.
 */
export function clearConvoArtifactPanel(convoId: string): void {
  memoryCache.delete(convoId);
  if (typeof window === 'undefined') {
    return;
  }
  userScopedStorage.removeItem(getConvoArtifactPanelKey(convoId));
}

/** Clears the in-memory artifact panel cache (for tests). */
export function clearConvoArtifactPanelMemoryCache(): void {
  memoryCache.clear();
}
